import { Component, ErrorInfo } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { notifyBugsnag } from "@/lib/bugsnag";
import { logger } from "@/lib/logger";
import { redactSensitive } from "@kudoz/shared/redactDiagnostics";

function postClientError(payload: {
  section: string;
  message: string;
  componentStack?: string | null;
}) {
  try {
    fetch("/api/debug/client-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...payload,
        // Error reports are telemetry, not navigation. Never forward an
        // invitation bearer token embedded in the current URL.
        url: redactSensitive(window.location.href),
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {});
  } catch {
    /* ignore fetch errors in error reporter */
  }
}

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logger.error(
      "[AppErrorBoundary] Uncaught render error:",
      error,
      info.componentStack
    );
    notifyBugsnag(error, { componentStack: info.componentStack });
    postClientError({
      section: "AppRoot",
      message: error.message,
      componentStack: info.componentStack,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-[60vh] gap-4 px-6 text-center">
          <AlertTriangle className="w-12 h-12 text-destructive" />
          <h2 className="text-lg font-semibold">Something went wrong</h2>
          <p className="text-sm text-muted-foreground max-w-xs">
            An unexpected error occurred. Try reloading the page — your data is
            safe.
          </p>
          <Button onClick={this.handleReload}>Reload</Button>
        </div>
      );
    }

    return this.props.children;
  }
}

interface SectionErrorBoundaryProps {
  children: React.ReactNode;
  section: string;
  fallback?: React.ReactNode;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class SectionErrorBoundary extends Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): SectionErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const { section } = this.props;
    logger.error(
      `[SectionErrorBoundary:${section}] Render error:`,
      error,
      info.componentStack
    );
    notifyBugsnag(error, { section, componentStack: info.componentStack });
    postClientError({
      section,
      message: error.message,
      componentStack: info.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      return (
        <div className="flex flex-col items-center justify-center py-8 px-4 text-center gap-2">
          <AlertTriangle className="w-6 h-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            This section failed to load.
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Try again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
